/**
 * Dependency Middleware Agent
 *
 * Central orchestrator that:
 * 1. Listens to field updates
 * 2. Resolves dependencies
 * 3. Produces batched updates
 * 4. Handles reset
 */

import { JSONSchema7 } from 'json-schema';
import {
  FieldUpdate,
  BatchedUpdate,
  DependencyManagerConfig,
  FieldPath,
} from './types';
import { dependencyGraphCache, topologicalSort } from './dependencyGraph';
import { getValueAtPath, setValueAtPath } from './batchedUpdate';
import { PendingValueTracker } from './pendingValueTracker';
import { AsyncHandlerManager } from './asyncHandlerManager';
import { PathResolver } from './pathResolver';

/**
 * Instantiates a template path by substituting [] placeholders with the
 * actual numeric indices extracted (in order) from the trigger field path.
 *
 * Example:
 *   template: "qualitiesInfo>[]>qualities>[]>propertyType>greatExpectations"
 *   trigger:  "qualitiesInfo>0>qualities>0>propertyType>engine"
 *   result:   "qualitiesInfo>0>qualities>0>propertyType>greatExpectations"
 *
 * If the template has more [] than the trigger has indices, the remaining []
 * are left as-is so the caller can fall back to full expansion.
 */
function instantiateTemplatePath(
  templatePath: FieldPath,
  triggerPath: FieldPath,
  separator: string,
): FieldPath {
  const triggerSegments = triggerPath.split(separator);
  const triggerIndices = triggerSegments.filter(s => /^\d+$/.test(s));

  let indexPointer = 0;
  return templatePath
    .split(separator)
    .map(seg => {
      if (seg === '[]' && indexPointer < triggerIndices.length) {
        return triggerIndices[indexPointer++];
      }
      return seg;
    })
    .join(separator);
}

/**
 * Dependency resolution handler
 * Defines how a field should be updated based on its dependencies
 */
export type DependencyHandler = (
  fieldPath: FieldPath,
  triggerFieldPath: FieldPath,
  triggerValue: any,
  currentFormData: any,
  config: DependencyManagerConfig,
) => any;

/**
 * Default dependency handlers for common patterns
 */
export const defaultDependencyHandlers: Record<string, DependencyHandler> = {
  /**
   * Resets a field to undefined when its dependency changes
   */
  reset: (_fieldPath, _triggerFieldPath, _triggerValue, _currentFormData) => {
    return undefined;
  },

  /**
   * Copies the value from the trigger field
   */
  copy: (_fieldPath, _triggerFieldPath, triggerValue) => {
    return triggerValue;
  },

  /**
   * Preserves the current value (no-op)
   */
  preserve: (
    fieldPath,
    _triggerFieldPath,
    _triggerValue,
    currentFormData,
    config,
  ) => {
    return getValueAtPath(currentFormData, fieldPath, config.idSeparator);
  },
};

/**
 * Dependency Middleware
 *
 * Main orchestrator for dependency resolution
 */
export class DependencyMiddleware {
  private id: string;
  private schema: JSONSchema7;
  private config: DependencyManagerConfig;
  private customHandlers: Map<FieldPath, DependencyHandler> = new Map();
  private defaultHandler: DependencyHandler;
  private pendingValues: PendingValueTracker = new PendingValueTracker(); // Unified pending value tracking
  private asyncManager: AsyncHandlerManager = new AsyncHandlerManager(); // Async handler promise tracking
  private pathResolver: PathResolver; // Template path expansion with caching
  // Store dynamically registered dependencies (not derived from schema)
  private dynamicDependencies: Map<FieldPath, Set<FieldPath>> = new Map();

  constructor(
    schema: JSONSchema7,
    config: DependencyManagerConfig,
    defaultHandler: DependencyHandler = defaultDependencyHandlers.preserve,
  ) {
    this.id = `middleware-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    this.schema = schema;
    this.config = config;
    this.defaultHandler = defaultHandler;
    this.pathResolver = new PathResolver(config.idSeparator);
  }

  getId(): string {
    return this.id;
  }

  /**
   * Gets the custom handlers map (for internal use by useDependencyManager)
   * @internal
   */
  getCustomHandlers(): Map<FieldPath, DependencyHandler> {
    return this.customHandlers;
  }

  /**
   * Registers a custom dependency handler for a specific field
   *
   * @param fieldPath - The field to register a handler for
   * @param handler - The handler function
   * @returns Tuple of [unregisterFunction, pendingValue]
   */
  registerHandler(
    fieldPath: FieldPath,
    handler: DependencyHandler,
  ): [() => void, any] {
    this.customHandlers.set(fieldPath, handler);

    // Check if there's a pending dependency value for this field
    // Don't mark as processed yet - that happens when the handler is called
    const pendingValue = this.pendingValues.get(fieldPath);

    return [
      () => {
        this.asyncManager.invalidate(fieldPath);
        this.customHandlers.delete(fieldPath);
        // Clean up pending value on unregister
        this.pendingValues.delete(fieldPath);
      },
      pendingValue,
    ];
  }

  /**
   * Registers dependencies for a field dynamically (at runtime)
   * This is used by components like IdentifierPicker that have built-in dependencies
   *
   * @param fieldPath - The field that has dependencies
   * @param dependsOn - Array of field paths this field depends on
   */
  registerDependencies(fieldPath: FieldPath, dependsOn: FieldPath[]): void {
    dependsOn.forEach(depField => {
      // Store in dynamicDependencies map: depField -> Set of dependents
      if (!this.dynamicDependencies.has(depField)) {
        this.dynamicDependencies.set(depField, new Set());
      }
      const dependents = this.dynamicDependencies.get(depField)!;
      if (!dependents.has(fieldPath)) {
        dependents.add(fieldPath);
      }
    });
  }

  /**
   * Gets the complete dependency graph including both schema-derived and dynamic dependencies
   * @internal
   */
  getCompleteDependencyGraph(): Record<FieldPath, FieldPath[]> {
    // Start with schema-derived dependencies from cache
    const graph = dependencyGraphCache.get(this.schema, this.config);

    // Merge in dynamic dependencies
    for (const [
      sourceField,
      dependents,
    ] of this.dynamicDependencies.entries()) {
      if (!graph[sourceField]) {
        graph[sourceField] = [];
      }
      // Add dynamic dependents that aren't already in the graph
      for (const dependent of dependents) {
        if (!graph[sourceField].includes(dependent)) {
          graph[sourceField].push(dependent);
        }
      }
    }

    return graph;
  }

  /**
   * Waits for all pending async handler updates and flushes them as a single batch
   * This ensures all async handlers resolve before applying updates
   */
  async flushPendingAsyncUpdates(): Promise<
    Array<{ path: FieldPath; value: any }>
  > {
    return this.asyncManager.waitAll();
  }

  /**
   * Processes a field update and returns a batched update with all dependent changes
   * organized by dependency levels for sequential processing
   *
   * @param update - The field update event
   * @param currentFormData - Current form data
   * @returns Batched update containing all changes organized by levels
   */
  processUpdate(update: FieldUpdate, currentFormData: any): BatchedUpdate {
    this.asyncManager.invalidate(update.fieldPath);
    const updates: Array<{ path: FieldPath; value: any }> = [];
    const levels: Array<Array<{ path: FieldPath; value: any }>> = [];

    // Always include the original update in level 0
    const level0 = [
      {
        path: update.fieldPath,
        value: update.value,
      },
    ];
    updates.push(level0[0]);
    levels.push(level0);

    // Handle reset intent
    if (update.intent === 'reset') {
      const dependentLevels = this.getDependentFieldsByLevel(
        update.fieldPath,
        currentFormData,
      );
      dependentLevels.forEach((levelDependents: FieldPath[]) => {
        const levelUpdates: Array<{ path: FieldPath; value: any }> = [];
        levelDependents.forEach((dependentPath: FieldPath) => {
          this.asyncManager.invalidate(dependentPath);
          const updateObj = {
            path: dependentPath,
            value: undefined,
          };
          updates.push(updateObj);
          levelUpdates.push(updateObj);
        });
        if (levelUpdates.length > 0) {
          levels.push(levelUpdates);
        }
      });
      return { updates, levels };
    }

    // Handle AI-fill intent (preserve user values, only fill empty fields)
    if (update.intent === 'ai-fill') {
      const dependentLevels = this.getDependentFieldsByLevel(
        update.fieldPath,
        currentFormData,
      );
      dependentLevels.forEach((levelDependents: FieldPath[]) => {
        const levelUpdates: Array<{ path: FieldPath; value: any }> = [];
        levelDependents.forEach((dependentPath: FieldPath) => {
          const currentValue = getValueAtPath(
            currentFormData,
            dependentPath,
            this.config.idSeparator,
          );
          // Only fill if field is empty
          if (
            currentValue === undefined ||
            currentValue === null ||
            currentValue === ''
          ) {
            const handler = this.getHandler(dependentPath);
            const newValue = handler(
              dependentPath,
              update.fieldPath,
              update.value,
              currentFormData,
              this.config,
            );
            const updateObj = {
              path: dependentPath,
              value: newValue,
            };
            updates.push(updateObj);
            levelUpdates.push(updateObj);
          }
        });
        if (levelUpdates.length > 0) {
          levels.push(levelUpdates);
        }
      });
      return { updates, levels };
    }

    // Normal update - resolve dependencies using topological sort.
    // Cascading applies for both 'user' and 'system'
    if (update.source === 'user' || update.source === 'system') {
      // Reset processed flags when a new dependency update arrives
      // This allows handlers to be called again with the new dependency value
      this.pendingValues.resetProcessedFlags();

      const graph = this.getCompleteDependencyGraph();

      // Get fields in topological order (dependencies before dependents)
      const sortedDependents = topologicalSort(update.fieldPath, graph);

      // Expand template paths to actual indices.
      // For schema-derived dependencies the graph stores template paths ([] placeholders).
      // First try to instantiate using the trigger field's own indices so that only
      // the sibling items are updated (e.g. qualities>0>… stays 0, not expanding to all).
      // Fall back to full expansion when instantiation leaves unconsumed [] placeholders.
      const expandedDependents: FieldPath[] = [];
      sortedDependents.forEach(templatePath => {
        if (templatePath.includes(`${this.config.idSeparator}[]`)) {
          const instantiated = instantiateTemplatePath(
            templatePath,
            update.fieldPath,
            this.config.idSeparator,
          );
          if (!instantiated.includes(`${this.config.idSeparator}[]`)) {
            // All [] were replaced — use the precise instantiated path
            expandedDependents.push(instantiated);
          } else {
            // Still has unresolved [] — fall back to full expansion
            const expanded = this.pathResolver.expandTemplatePath(
              instantiated,
              currentFormData,
            );
            expandedDependents.push(...expanded);
          }
        } else {
          expandedDependents.push(templatePath);
        }
      });

      // Process each dependent in topological order
      // Use a working copy of formData that accumulates sync handler results.
      // This ensures downstream handlers see intermediate values from upstream
      // handlers in the same cascade (e.g., solutionObject can see solutionUrn's
      // resolved value even though both are processed in the same pass).
      let workingFormData = currentFormData;

      expandedDependents.forEach((dependentPath: FieldPath) => {
        this.asyncManager.invalidate(dependentPath);
        // Store the dependency value for late registration
        this.pendingValues.set(dependentPath, update.value);

        // Only call handler if it's registered
        if (this.customHandlers.has(dependentPath)) {
          const handler = this.getHandler(dependentPath);

          // Call handler
          const newValue = handler(
            dependentPath,
            update.fieldPath,
            update.value,
            workingFormData,
            this.config,
          );

          // Mark as processed
          this.pendingValues.markProcessed(dependentPath);

          // Handle sync vs async
          if (newValue instanceof Promise) {
            // Async handler
            this.asyncManager.add(dependentPath, newValue);
          } else {
            // Sync handler - add to updates
            updates.push({
              path: dependentPath,
              value: newValue,
            });
            // Update working formData so downstream handlers see this value
            workingFormData = setValueAtPath(
              workingFormData,
              dependentPath,
              newValue,
              this.config.idSeparator,
            );
          }
        }
      });

      // Note: levels array is no longer used with topological sort
      // All updates are now in a single flat list in the correct order
    }

    return { updates, levels };
  }

  /**
   * Gets pending async updates (without clearing them)
   *
   * @returns Array of promises that resolve to {path, value} tuples
   */
  getPendingAsyncUpdates(): Array<Promise<{ path: FieldPath; value: any }>> {
    return this.asyncManager.getPendingPromises();
  }

  /**
   * Gets the async handler manager (for internal use by useDependencyManager)
   * @internal
   */
  getAsyncManager(): AsyncHandlerManager {
    return this.asyncManager;
  }

  /**
   * Gets dependent fields organized by levels for reset/ai-fill intents
   * Uses topological sort to get dependents in correct order
   *
   * @param triggerFieldPath - The field that triggered the update
   * @param currentFormData - Current form data for path expansion
   * @returns Array of levels, each containing field paths
   */
  private getDependentFieldsByLevel(
    triggerFieldPath: FieldPath,
    currentFormData: any,
  ): FieldPath[][] {
    const graph = this.getCompleteDependencyGraph();
    const sortedDependents = topologicalSort(triggerFieldPath, graph);

    // Expand template paths to actual indices (same logic as processUpdate)
    const expandedDependents: FieldPath[] = [];
    sortedDependents.forEach(templatePath => {
      if (templatePath.includes(`${this.config.idSeparator}[]`)) {
        const instantiated = instantiateTemplatePath(
          templatePath,
          triggerFieldPath,
          this.config.idSeparator,
        );
        if (!instantiated.includes(`${this.config.idSeparator}[]`)) {
          expandedDependents.push(instantiated);
        } else {
          const expanded = this.pathResolver.expandTemplatePath(
            instantiated,
            currentFormData,
          );
          expandedDependents.push(...expanded);
        }
      } else {
        expandedDependents.push(templatePath);
      }
    });

    // Return as a single level (topological sort already ensures correct order)
    return expandedDependents.length > 0 ? [expandedDependents] : [];
  }

  /**
   * Gets the appropriate handler for a field
   *
   * @param fieldPath - The field path
   * @returns The handler function
   */
  private getHandler(fieldPath: FieldPath): DependencyHandler {
    return this.customHandlers.get(fieldPath) || this.defaultHandler;
  }

  /**
   * Gets the pending value tracker (for internal use by useDependencyManager)
   * @internal
   */
  getPendingValueTracker(): PendingValueTracker {
    return this.pendingValues;
  }

  /**   * Updates the schema
   *
   * @param newSchema - The new schema
   */
  updateSchema(newSchema: JSONSchema7): void {
    this.schema = newSchema;
    dependencyGraphCache.clear();
  }
}
